import { AUTHZ_RECORD_ACTIONS } from '../constants/authz/features.constants';

const RECORD_ACTION_CODENAMES = Object.values(AUTHZ_RECORD_ACTIONS);

const VIEW_INHERIT_ACTIONS = [
  AUTHZ_RECORD_ACTIONS.VIEW,
  AUTHZ_RECORD_ACTIONS.DOWNLOAD,
  AUTHZ_RECORD_ACTIONS.VIEW_STATUS_LOGS,
  AUTHZ_RECORD_ACTIONS.VIEW_IMAGES,
  AUTHZ_RECORD_ACTIONS.QUICK_CALL,
  AUTHZ_RECORD_ACTIONS.ADD_COMMENT,
  AUTHZ_RECORD_ACTIONS.ASSIGN,
];

const EDIT_INHERIT_ACTIONS = [
  AUTHZ_RECORD_ACTIONS.CREATE,
  AUTHZ_RECORD_ACTIONS.IMPORT,
  AUTHZ_RECORD_ACTIONS.UPDATE,
  AUTHZ_RECORD_ACTIONS.MOVE,
  AUTHZ_RECORD_ACTIONS.DELETE,
  AUTHZ_RECORD_ACTIONS.SEND_TEMPLATE,
  AUTHZ_RECORD_ACTIONS.CHAT,
  AUTHZ_RECORD_ACTIONS.MANAGE_ADMIN,
];

function emptyActions() {
  return RECORD_ACTION_CODENAMES.reduce((acc, codename) => {
    acc[codename] = false;
    return acc;
  }, {});
}

function normalizeCampaignId(value) {
  return String(value || '').trim().toUpperCase();
}

function findRecordActionsEntry(explicitMap, campaignKey) {
  if (!campaignKey || !explicitMap || typeof explicitMap !== 'object') {
    return null;
  }
  if (Object.prototype.hasOwnProperty.call(explicitMap, campaignKey)) {
    return { key: campaignKey, stored: explicitMap[campaignKey] || {} };
  }
  const normalized = normalizeCampaignId(campaignKey);
  const matchedKey = Object.keys(explicitMap).find(
    (key) => normalizeCampaignId(key) === normalized
  );
  if (matchedKey && Object.prototype.hasOwnProperty.call(explicitMap, matchedKey)) {
    return { key: matchedKey, stored: explicitMap[matchedKey] || {} };
  }
  return null;
}

function campaignScopeFromGrants(grants, campaignId, accountId) {
  const resources = grants?.resources || {};
  const campaignKey = String(campaignId || '').trim();
  if (!campaignKey) {
    return { canView: false, canEdit: false };
  }

  const campaignNorm = normalizeCampaignId(campaignKey);
  const campaignEntry = (resources.campaigns || []).find(
    (item) => normalizeCampaignId(item.id) === campaignNorm
  );
  if (campaignEntry) {
    const canEdit = Boolean(campaignEntry.can_edit);
    return {
      canView: Boolean(campaignEntry.can_view || canEdit),
      canEdit,
    };
  }

  if (accountId) {
    const accountEntry = (resources.accounts || []).find(
      (item) => String(item.id) === String(accountId)
    );
    if (accountEntry) {
      const canEdit = Boolean(accountEntry.can_edit);
      return {
        canView: Boolean(accountEntry.can_view || canEdit),
        canEdit,
      };
    }
  }

  return { canView: false, canEdit: false };
}

function buildInheritedActions(canView, canEdit) {
  const actions = emptyActions();
  if (canView) {
    VIEW_INHERIT_ACTIONS.forEach((codename) => {
      actions[codename] = true;
    });
  }
  if (canEdit) {
    EDIT_INHERIT_ACTIONS.forEach((codename) => {
      actions[codename] = true;
    });
  }
  return actions;
}

export function getEffectiveRecordActions(authz, campaignId, accountId) {
  const grants = authz?.grants || {};
  const campaignKey = String(campaignId || '').trim();
  if (!campaignKey) {
    return emptyActions();
  }

  const explicitMap = grants.record_actions || {};
  const explicitEntry = findRecordActionsEntry(explicitMap, campaignKey);
  if (explicitEntry) {
    const actions = emptyActions();
    const stored = explicitEntry.stored;
    RECORD_ACTION_CODENAMES.forEach((codename) => {
      actions[codename] = Boolean(stored[codename]);
    });
    return actions;
  }

  const { canView, canEdit } = campaignScopeFromGrants(grants, campaignKey, accountId);
  return buildInheritedActions(canView, canEdit);
}

/**
 * @param {import('./types/authz.types').AuthzContext} authz
 * @param {string} campaignId
 * @param {string} codename
 * @param {string} [accountId]
 */
export function hasRecordAction(authz, campaignId, codename, accountId) {
  if (!authz || !campaignId || !codename) {
    return false;
  }
  if (authz.isFullAccess) {
    return true;
  }
  if (!authz.isAuthzV2Enabled) {
    return true;
  }

  const effective = getEffectiveRecordActions(authz, campaignId, accountId);
  return Boolean(effective[codename]);
}

export function canImportRecordsFromGrants(authz, campaignId, accountId) {
  return hasRecordAction(authz, campaignId, AUTHZ_RECORD_ACTIONS.CREATE, accountId);
}

export function canExportRecordsFromGrants(authz, campaignId, accountId) {
  return hasRecordAction(authz, campaignId, AUTHZ_RECORD_ACTIONS.VIEW, accountId);
}

export function campaignHasExplicitRecordGrants(authz, campaignId) {
  const explicitMap = authz?.grants?.record_actions || {};
  const campaignKey = String(campaignId || '').trim();
  return Boolean(findRecordActionsEntry(explicitMap, campaignKey));
}
